/**
 * useAdaptiveCardHandler — Handles Adaptive Card Action.Submit interactions
 * Extracted VERBATIM from SimpleCopilotPanel.tsx L1255-1394
 * 
 * Risks handled:
 * - R4: normalizeApiResponse + isValidAdaptiveCard imported from shared responseUtils
 * - R11: controlRef not needed here (no imperative calls)
 * - R23: Inline user message at L1297-1302 — PRE-EXISTING, copied verbatim (doesn't use createUserMessage)
 * 
 * SAFE REFACTORING: No logic changes. Code copied exactly as-is.
 * All state received as params from orchestrator. No state ownership.
 */

import * as React from 'react';
import { postExternalConsumptionAPI } from '../../../utils/httpUtilsExternalAPI';
import { COPILOT_CONSTANTS } from '../constants';
import type { Message } from '../interfaces';
import type { CopilotChatParams } from './CopilotChatParams';
import { normalizeApiResponse, isValidAdaptiveCard } from '../utils/responseUtils';
import { createBotMessage } from '../utils/messageUtils';
import { formatAndConvertByType } from '../utils/formatAndConvertByType';
import { extractTicketNumber } from '../utils/ticketExtraction';

/**
 * Hook for handling Adaptive Card Action.Submit events
 * Returns the handleAdaptiveCardAction callback
 */
export function useAdaptiveCardHandler(params: CopilotChatParams) {
  const {
    loading,
    setMessages,
    setLoading,
    setFollowUpQuestions,
    setCurrentTicketNumber,
    showExitConfirmation,
    setShowExitConfirmation,
    sessionIdRef,
    lastAnchoredMessageIdRef,
    effectiveApi,
    userEmail,
    mcapsTelemetry,
    platformAuthClient,
  } = params;
  const renderedConversationId = sessionIdRef.current;

  // Verbatim from SimpleCopilotPanel.tsx L1255-1394
  const handleAdaptiveCardAction = React.useCallback(async (actionData: any, messageId: string) => {
    if (!actionData) return;
    if (sessionIdRef.current !== renderedConversationId) return;
    const isCurrent = () => sessionIdRef.current === renderedConversationId;

    try {
      // Prevent duplicate submissions while already processing
      if (loading) {
        return;
      }

      // Log telemetry for adaptive card action
      mcapsTelemetry.logComponentInteraction({
        component: 'AdaptiveCardActionSubmit',
        action: 'submit',
        message: `Adaptive card action for message ${messageId}`
      });

      // Extract user input from action data
      // Adaptive Cards SDK automatically merges input values (e.g., userResponse) into actionData
      const userInput = actionData.userResponse || actionData.query || actionData.userMessage || '';
      
      // Validate user input
      if (!userInput || typeof userInput !== 'string' || userInput.trim().length === 0) {
        mcapsTelemetry.logError({
          component: 'SimpleCopilotPanel',
          action: 'submit',
          error: 'Empty or invalid user input'
        });
        return;
      }
      
      // Extract sessionId from action data or use current session
      const sessionId = actionData.sessionId || sessionIdRef.current;
      
      // Check exit confirmation state (same as handleSendMessage)
      const isExitConfirmation = showExitConfirmation;
      
      // Clear exit confirmation if active
      if (isExitConfirmation) {
        setShowExitConfirmation(false);
      }
      
      // Add user message showing what the user typed
      // R23: Inline user message (PRE-EXISTING — doesn't use createUserMessage helper). Copy verbatim.
      const userMessage: Message = {
        id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.USER + Date.now(),
        role: 'user',
        content: userInput.trim(),
        timestamp: Date.now()
      };
      // Treat this as the active anchor question as well
      lastAnchoredMessageIdRef.current = userMessage.id;
      setMessages(prev => [...prev, userMessage]);

      // Set loading state
      setLoading(true);

      // Send action data to API using existing API call pattern
      if (!effectiveApi) {
        throw new Error(COPILOT_CONSTANTS.API.ERROR_MESSAGES.API_NOT_CONFIGURED);
      }

      // Include appName context for ECIF integration
      const appName = sessionStorage.getItem('mcapsCopilot_appName');
      const response = await postExternalConsumptionAPI(
        effectiveApi,
        {
          query: userInput,
          sessionId: sessionId,
          // Include cancelForm parameter if exit confirmation was active
          ...(isExitConfirmation && { cancelForm: true }),
          ...(appName && { appName: appName })
        },
        platformAuthClient
      );
      if (!isCurrent()) return;

      if (response && response.status >= 200 && response.status < 300) {
        let data = response.data || response;
        
        // Apply same normalization as handleSendMessage
        data = normalizeApiResponse(data, 'handleAdaptiveCardAction');
        
        // Detect and validate Adaptive Card
        const isAdaptiveCard = data && 
          typeof data === 'object' && 
          data.type === 'AdaptiveCard' && 
          data.version &&
          isValidAdaptiveCard(data);
        
        if (data && data.type === 'AdaptiveCard' && !isAdaptiveCard) {
          mcapsTelemetry.logError({
            component: 'SimpleCopilotPanel',
            action: 'submit',
            error: 'Invalid Adaptive Card schema'
          });
        }
        
        const answerText = isAdaptiveCard ? '' : (data.Message || data.response || '');
        
        // Extract ticket number from Adaptive Card response
        const extractedTicketNumber = extractTicketNumber(answerText || JSON.stringify(data), data);
        if (extractedTicketNumber) {
          setCurrentTicketNumber(extractedTicketNumber);
          sessionStorage.setItem('mcapsCopilot_ticketNumber', extractedTicketNumber);
        } else {
        }
        
        // Format and create bot message using the same flow as handleSendMessage
        const formatted = formatAndConvertByType(data);
        const botMessage = createBotMessage(answerText, data, formatted);

        setMessages(prev => [...prev, botMessage]);
        
        // Update follow-up questions
        const newFollowUpQuestions = data.followUpQuestions || data.followupQuestions || [];
        setFollowUpQuestions(newFollowUpQuestions);
      } else {
        throw new Error('Failed to process adaptive card action');
      }
    } catch (error: any) {
      if (!isCurrent()) return;
      mcapsTelemetry.logError({
        component: 'SimpleCopilotPanel',
        action: 'submit',
        error: error.message || 'Unknown error in Adaptive Card action'
      });

      // Show error message
      const errorMessage: Message = {
        id: COPILOT_CONSTANTS.MESSAGE_ID_PREFIXES.ERROR + Date.now(),
        role: 'error',
        content: 'Failed to process card action. Please try again.',
        timestamp: Date.now(),
        meta: {
          originalError: error.message,
          responseType: 'error'
        }
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [effectiveApi, userEmail, platformAuthClient, mcapsTelemetry, showExitConfirmation, renderedConversationId]);

  return { handleAdaptiveCardAction };
}

// Generated by Copilot
